// src/components/Login.tsx - Coach-Style UX Redesign
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { axiosInstance, getTokenExpiryTime, issueRefreshToken } from '../utils/axiosConfig';
import { validateEmail, validatePassword, collectErrors } from '../utils/validation';
import { trackEvent } from '../utils/trackEvent';
import { getCaptchaToken } from '../utils/captcha';
import '../styles/Login.css';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [mfaAttempts, setMfaAttempts] = useState(0);
  const [mfaMethod, setMfaMethod] = useState<'authenticator' | 'sms' | null>(null);
  const [phone, setPhone] = useState('');
  const [smsSent, setSmsSent] = useState(false);
  const navigate = useNavigate();
  const { setIsAuthenticated } = useAuth();

  // CSRF token initialization
  useEffect(() => {
    const initializeCsrfToken = async () => {
      try {
        await axiosInstance.get('/health');
      } catch (error) {
        // Token initialization failed, but we'll try anyway on login
        console.warn('CSRF token initialization failed, will attempt login');
      }
    };
    initializeCsrfToken();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Client-side validation mirrors the backend rules for instant feedback.
    const { ok, firstMessage } = collectErrors({
      email: validateEmail(email),
      password: validatePassword(password),
    });
    if (!ok) {
      toast.error(firstMessage || 'Please fix the highlighted fields');
      return;
    }

    setLoading(true);

    try {
      // Phase 20.1 — captchaToken is null (and omitted server-side) until
      // REACT_APP_RECAPTCHA_SITE_KEY is configured.
      const captchaToken = await getCaptchaToken('login');
      const response = await axiosInstance.post('/auth/login', {
        email: email.trim(),
        password,
        rememberMe,
        ...(captchaToken ? { captchaToken } : {}),
      });

      if (response.data.mfaRequired) {
        setMfaRequired(true);
        setMfaToken(response.data.mfaToken);
        toast.success('Enter your 2FA code');
        setLoading(false);
        return;
      }

      const token = response.data.token;
      localStorage.setItem('token', token);
      trackEvent('login', { method: 'password' });
      // Phase 16 — mint a refresh token for silent session renewal
      void issueRefreshToken(rememberMe);

      const expiryTime = getTokenExpiryTime(token);
      if (expiryTime) {
        // Token expiry now comes from backend (1h normal, 30d with rememberMe)
        localStorage.setItem('tokenExpiry', expiryTime.toString());
        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
        }
      }

      // Per UC-1.2 Step 5.1: prompt MFA enrollment on login if not enabled.
      if (response.data.mfaSuggestionRequired) {
        localStorage.setItem('mfaSuggestionPending', '1');
      }

      setIsAuthenticated(true);
      toast.success('Welcome back! 🎉');
      navigate('/dashboard');
    } catch (err: any) {
      // Phase 1 fix: surface server-provided messages even when the body
      // is plain text (e.g. an old rate-limit response) instead of always
      // defaulting to the generic "check your credentials" string.
      const data = err.response?.data;
      const msg =
        (data && typeof data === 'object' && (data.error || data.message)) ||
        (typeof data === 'string' && data) ||
        err.response?.statusText ||
        err.message ||
        'Login failed. Please check your credentials.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleMFASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Rate limiting check
    if (mfaAttempts >= 5) {
      toast.error('Too many attempts. Please wait 5 minutes before trying again.');
      return;
    }
    
    setLoading(true);

    try {
      const response = await axiosInstance.post('/auth/login-mfa', 
        { code: mfaCode, rememberMe },
        {
          headers: { Authorization: `Bearer ${mfaToken}` },
        }
      );

      const token = response.data.token;
      localStorage.setItem('token', token);
      // Phase 16 — mint a refresh token for silent session renewal
      void issueRefreshToken(rememberMe);

      const expiryTime = getTokenExpiryTime(token);
      if (expiryTime) {
        let adjustedExpiry = expiryTime;
        
        if (rememberMe) {
          adjustedExpiry = Date.now() + 30 * 24 * 60 * 60 * 1000;
          localStorage.setItem('rememberMe', 'true');
        }
        
        localStorage.setItem('tokenExpiry', adjustedExpiry.toString());
      }
      
      setIsAuthenticated(true);
      toast.success('Authentication successful!');
      navigate('/dashboard');
    } catch (err: any) {
      setMfaAttempts(prev => prev + 1);
      const error = err.response?.data?.error || 'Invalid code. Please try again.';
      toast.error(error);
      
      if (mfaAttempts + 1 >= 5) {
        toast.error('Account locked. Please wait 5 minutes.');
        setTimeout(() => setMfaAttempts(0), 5 * 60 * 1000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackupCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axiosInstance.post('/auth/login-backup-code', {
        email,
        code: backupCode.trim(),
      });

      const token = response.data.token;
      localStorage.setItem('token', token);
      // Phase 16 — mint a refresh token for silent session renewal
      void issueRefreshToken(rememberMe);

      const expiryTime = getTokenExpiryTime(token);
      if (expiryTime) {
        let adjustedExpiry = expiryTime;
        
        if (rememberMe) {
          adjustedExpiry = Date.now() + 30 * 24 * 60 * 60 * 1000;
          localStorage.setItem('rememberMe', 'true');
        }
        
        localStorage.setItem('tokenExpiry', adjustedExpiry.toString());
      }
      
      setIsAuthenticated(true);
      toast.success('Backup code verified! You have ' + (response.data.warning || 'some backup codes remaining'));
      navigate('/dashboard');
    } catch (err: any) {
      const error = err.response?.data?.error || 'Invalid backup code. Please try again.';
      toast.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendSMS = async () => {
    if (!phone.match(/^\+?[1-9]\d{1,14}$/)) {
      toast.error('Enter a valid phone number with country code (e.g., +1 555 123 4567)');
      return;
    }
    setLoading(true);
    try {
      await axiosInstance.post('/auth/login-mfa-sms', 
        { phone },
        { headers: { Authorization: `Bearer ${mfaToken}` } }
      );
      setSmsSent(true);
      toast.success('SMS code sent to your phone!');
    } catch (err: any) {
      const error = err.response?.data?.error || 'Failed to send SMS code';
      toast.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSMSVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (mfaAttempts >= 5) {
      toast.error('Too many attempts. Please wait 5 minutes before trying again.');
      return;
    }
    
    setLoading(true);

    try {
      const response = await axiosInstance.post('/auth/login-mfa-sms-verify', 
        { code: mfaCode },
        {
          headers: { Authorization: `Bearer ${mfaToken}` },
        }
      );

      const token = response.data.token;
      localStorage.setItem('token', token);
      // Phase 16 — mint a refresh token for silent session renewal
      void issueRefreshToken(rememberMe);

      const expiryTime = getTokenExpiryTime(token);
      if (expiryTime) {
        let adjustedExpiry = expiryTime;
        
        if (rememberMe) {
          adjustedExpiry = Date.now() + 30 * 24 * 60 * 60 * 1000;
          localStorage.setItem('rememberMe', 'true');
        }
        
        localStorage.setItem('tokenExpiry', adjustedExpiry.toString());
      }
      
      setIsAuthenticated(true);
      toast.success('Authentication successful!');
      navigate('/dashboard');
    } catch (err: any) {
      setMfaAttempts(prev => prev + 1);
      const error = err.response?.data?.error || 'Invalid code. Please try again.';
      toast.error(error);
      
      if (mfaAttempts + 1 >= 5) {
        toast.error('Account locked. Please wait 5 minutes.');
        setTimeout(() => setMfaAttempts(0), 5 * 60 * 1000);
      }
    } finally {
      setLoading(false);
    }
  };

  if (mfaRequired) {
    // Backup code entry view
    if (useBackupCode) {
      return (
        <div className="hb-login-container">
          <div className="hb-login-card hb-scale-in">
            <div className="hb-login-header">
              <h1 className="hb-login-title">Use Backup Code</h1>
              <p className="hb-login-subtitle">Enter one of your backup recovery codes</p>
            </div>

            <form onSubmit={handleBackupCodeSubmit} className="hb-form">
              <div className="hb-input-group">
                <label htmlFor="backupCode" className="hb-label hb-label-required">
                  Backup Code
                </label>
                <input
                  id="backupCode"
                  type="text"
                  value={backupCode}
                  onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                  placeholder="XXXX-XXXX-XXXX"
                  required
                  className="hb-input"
                />
                <span className="hb-helper">Enter a backup code from your saved list</span>
              </div>

              <div className="hb-form-actions">
                <button
                  type="submit"
                  disabled={loading || backupCode.length < 8}
                  className="hb-btn hb-btn-primary hb-btn-block"
                >
                  {loading ? (
                    <>
                      <span className="hb-loader"></span>
                      Verifying...
                    </>
                  ) : (
                    'Verify Backup Code'
                  )}
                </button>
              </div>

              <div className="hb-form-actions">
                <button
                  type="button"
                  onClick={() => {
                    setUseBackupCode(false);
                    setBackupCode('');
                  }}
                  className="hb-btn hb-btn-ghost hb-btn-block"
                >
                  ← Back to Authenticator Code
                </button>
              </div>
            </form>
          </div>
        </div>
      );
    }

    // MFA method selection view (initial)
    if (!mfaMethod) {
      return (
        <div className="hb-login-container">
          <div className="hb-login-card hb-scale-in">
            <div className="hb-login-header">
              <h1 className="hb-login-title">Choose Verification Method</h1>
              <p className="hb-login-subtitle">Select how you'd like to verify your identity</p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
              <button
                type="button"
                onClick={() => setMfaMethod('authenticator')}
                disabled={loading}
                style={{
                  padding: '14px',
                  background: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '1em',
                }}
              >
                📱 Authenticator App
              </button>
              <button
                type="button"
                onClick={() => setMfaMethod('sms')}
                disabled={loading}
                style={{
                  padding: '14px',
                  background: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '1em',
                }}
              >
                💬 SMS Code
              </button>
            </div>

            <div className="hb-form-actions">
              <button
                type="button"
                onClick={() => {
                  setMfaRequired(false);
                  setMfaCode('');
                  setMfaToken('');
                }}
                className="hb-btn hb-btn-ghost hb-btn-block"
              >
                ← Back to Login
              </button>
            </div>
          </div>
        </div>
      );
    }

    // SMS verification view
    if (mfaMethod === 'sms') {
      return (
        <div className="hb-login-container">
          <div className="hb-login-card hb-scale-in">
            <div className="hb-login-header">
              <h1 className="hb-login-title">SMS Verification</h1>
              <p className="hb-login-subtitle">
                {smsSent ? 'Enter the code we sent to your phone' : 'Enter your phone number'}
              </p>
            </div>

            {!smsSent ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendSMS();
                }}
                className="hb-form"
              >
                <div className="hb-input-group">
                  <label htmlFor="phone" className="hb-label hb-label-required">
                    Phone Number
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 555 123 4567"
                    required
                    className="hb-input"
                  />
                  <span className="hb-helper">Include country code (e.g., +1 for USA)</span>
                </div>

                <div className="hb-form-actions">
                  <button
                    type="submit"
                    disabled={loading || !phone}
                    className="hb-btn hb-btn-primary hb-btn-block"
                  >
                    {loading ? (
                      <>
                        <span className="hb-loader"></span>
                        Sending...
                      </>
                    ) : (
                      'Send Code'
                    )}
                  </button>
                </div>

                <div className="hb-form-actions">
                  <button
                    type="button"
                    onClick={() => {
                      setMfaMethod(null);
                      setPhone('');
                      setMfaCode('');
                    }}
                    className="hb-btn hb-btn-ghost hb-btn-block"
                  >
                    ← Back to Method Selection
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleSMSVerify} className="hb-form">
                <div className="hb-input-group">
                  <label htmlFor="smsCode" className="hb-label hb-label-required">
                    Verification Code
                  </label>
                  <input
                    id="smsCode"
                    type="text"
                    maxLength={6}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    required
                    className="hb-input hb-mfa-input"
                  />
                  <span className="hb-helper">Enter the 6-digit code sent to {phone}</span>
                </div>

                <div className="hb-checkbox-group">
                  <input
                    id="rememberMeSMS"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="hb-checkbox"
                  />
                  <label htmlFor="rememberMeSMS" className="hb-checkbox-label">
                    Remember me for 30 days
                  </label>
                </div>

                <div className="hb-form-actions">
                  <button
                    type="submit"
                    disabled={loading || mfaCode.length !== 6}
                    className="hb-btn hb-btn-primary hb-btn-block"
                  >
                    {loading ? (
                      <>
                        <span className="hb-loader"></span>
                        Verifying...
                      </>
                    ) : (
                      'Verify Code'
                    )}
                  </button>
                </div>

                <div className="hb-form-actions">
                  <button
                    type="button"
                    onClick={() => {
                      setSmsSent(false);
                      setMfaCode('');
                    }}
                    className="hb-btn hb-btn-ghost hb-btn-block"
                  >
                    ← Resend Code
                  </button>
                </div>

                <div className="hb-mfa-alternate">
                  <p className="hb-text-muted">
                    Lost access to your phone?{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setUseBackupCode(true);
                        setMfaMethod(null);
                        setPhone('');
                        setSmsSent(false);
                        setMfaCode('');
                      }}
                      className="hb-link-primary hb-btn-link"
                    >
                      Use a backup code
                    </button>
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      );
    }

    // Authenticator (TOTP) verification view
    if (mfaMethod === 'authenticator') {
      return (
        <div className="hb-login-container">
          <div className="hb-login-card hb-scale-in">
            <div className="hb-login-header">
              <h1 className="hb-login-title">Two-Factor Authentication</h1>
              <p className="hb-login-subtitle">Enter the 6-digit code from your authenticator app</p>
            </div>

            <form onSubmit={handleMFASubmit} className="hb-form">
              <div className="hb-input-group">
                <label htmlFor="mfaCode" className="hb-label hb-label-required">
                  Authentication Code
                </label>
                <input
                  id="mfaCode"
                  type="text"
                  maxLength={6}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  required
                  className="hb-input hb-mfa-input"
                />
                <span className="hb-helper">Enter the 6-digit code from your app</span>
              </div>

              <div className="hb-checkbox-group">
                <input
                  id="rememberMeMFA"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="hb-checkbox"
                />
                <label htmlFor="rememberMeMFA" className="hb-checkbox-label">
                  Remember me for 30 days
                </label>
              </div>

              <div className="hb-form-actions">
                <button
                  type="submit"
                  disabled={loading || mfaCode.length !== 6}
                  className="hb-btn hb-btn-primary hb-btn-block"
                >
                  {loading ? (
                    <>
                      <span className="hb-loader"></span>
                      Verifying...
                    </>
                  ) : (
                    'Verify Code'
                  )}
                </button>
              </div>

              <div className="hb-form-actions">
                <button
                  type="button"
                  onClick={() => {
                    setMfaMethod(null);
                    setMfaCode('');
                  }}
                  className="hb-btn hb-btn-ghost hb-btn-block"
                >
                  ← Back to Method Selection
                </button>
              </div>

              <div className="hb-mfa-alternate">
                <p className="hb-text-muted">
                  Lost access to your authenticator?{' '}
                  <button
                    type="button"
                    onClick={() => setUseBackupCode(true)}
                    className="hb-link-primary hb-btn-link"
                  >
                    Use a backup code
                  </button>
                </p>
              </div>
            </form>
          </div>
        </div>
      );
    }
  }

  return (
    <div className="hb-login-container hb-auth-split">
      {/* Welcoming side panel (visual only — form logic unchanged) */}
      <aside className="hb-auth-aside">
        <div className="hb-auth-orbs" aria-hidden="true">
          <span /><span /><span />
        </div>
        <div className="hb-auth-aside-inner">
          <div className="hb-auth-brand">
            <span className="hb-auth-brand-mark">S</span> Sumbitrbuddy
          </div>
          <h2 className="hb-auth-headline">
            Welcome back to your{" "}
            <span className="hb-auth-accent">career command center</span>.
          </h2>
          <p className="hb-auth-lead">
            Every application, interview, and offer — tracked in one place.
            Pick up right where you left off.
          </p>
          <ul className="hb-auth-points">
            <li>AI-matched roles, scored to you</li>
            <li>Applications &amp; replies tracked automatically</li>
            <li>Interview prep the moment it counts</li>
          </ul>
          <div className="hb-auth-chips">
            <span className="hb-auth-chip">94% match · Product Designer</span>
            <span className="hb-auth-chip hb-auth-chip-b">Applied 2m ago</span>
          </div>
        </div>
      </aside>

      <div className="hb-auth-main">
      <div className="hb-login-card hb-fade-in">
        <div className="hb-login-header">
          <h1 className="hb-login-title">Welcome Back</h1>
          <p className="hb-login-subtitle">Your job search, supported</p>
        </div>

        <form onSubmit={handleSubmit} className="hb-form">
          <div className="hb-input-group">
            <label htmlFor="email" className="hb-label hb-label-required">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="hb-input"
              autoComplete="email"
            />
          </div>

          <div className="hb-input-group">
            <label htmlFor="password" className="hb-label hb-label-required">
              Password
            </label>
            <div className="hb-password-wrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                className="hb-input"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="hb-password-toggle"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          <div className="hb-form-row">
            <div className="hb-checkbox-group">
              <input
                id="rememberMe"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="hb-checkbox"
              />
              <label htmlFor="rememberMe" className="hb-checkbox-label">
                Remember me
              </label>
            </div>

            <Link to="/forgot-password" className="hb-link-subtle">
              Forgot password?
            </Link>
          </div>

          <div className="hb-form-actions">
            <button
              type="submit"
              disabled={loading}
              className="hb-btn hb-btn-primary hb-btn-block hb-btn-lg"
            >
              {loading ? (
                <>
                  <span className="hb-loader"></span>
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </div>
        </form>

        <div className="hb-login-footer">
          <p className="hb-text-muted">
            Don't have an account?{' '}
            <Link to="/signup" className="hb-link-primary">
              Create your free account
            </Link>
          </p>
        </div>
      </div>

      <div className="hb-coach-message hb-mt-lg">
        Every job search starts with one step. You're on track.
      </div>
      </div>
    </div>
  );
};

export default Login;